/**
 * Analiz için birden fazla GTFS tablosunu birleştirir.
 * Birden fazla feed varsa ID alanlarını `{localId}::{id}` ile namespace'ler.
 *
 * Bellek: feed'leri tek tek `appendFeedTablesToMerged` ile ekleyin;
 * kaynak tabloları append sonrası serbest bırakın.
 */

const ID_FIELDS = new Set([
  'route_id',
  'stop_id',
  'trip_id',
  'service_id',
  'shape_id',
  'agency_id',
  'block_id',
  'parent_station',
])

/**
 * @param {string} localId
 * @param {unknown} id
 */
export function namespaceGtfsId(localId, id) {
  if (id == null || id === '') return id
  return `${localId}::${id}`
}

/**
 * @param {Record<string, unknown>} row
 * @param {string} localId
 * @param {{ name?: string, dbId?: number|null }} meta
 * @param {boolean} applyNamespace
 * @param {string[]|null} cols
 */
function rewriteRow(row, localId, meta, applyNamespace, cols = null) {
  /** @type {Record<string, unknown>} */
  const out = {}
  if (cols?.length) {
    for (const c of cols) {
      const v = row[c]
      if (v != null && v !== '') out[c] = v
    }
  } else {
    Object.assign(out, row)
  }
  if (applyNamespace) {
    for (const key of Object.keys(out)) {
      if (ID_FIELDS.has(key) && out[key] != null && out[key] !== '') {
        out[key] = namespaceGtfsId(localId, out[key])
      }
    }
  }
  out._feed_local_id = localId
  out._feed_name = meta.name || localId
  if (meta.dbId != null) out._feed_db_id = meta.dbId
  return out
}

/**
 * Tek feed'i birikimli merge hedefine ekler (in-place).
 * @param {Record<string, Record<string, unknown>[]>} acc
 * @param {{
 *   localId: string
 *   name?: string
 *   dbId?: number|null
 *   tables: Record<string, Record<string, unknown>[]>
 * }} bundle
 * @param {{
 *   namespaced?: boolean
 *   tableCols?: Record<string, string[]>
 *   tableNames?: string[]
 * }} [opts]
 */
export function appendFeedTablesToMerged(acc, bundle, opts = {}) {
  if (!acc || !bundle?.localId || !bundle.tables) return acc
  const namespaced = Boolean(opts.namespaced)
  const tableNames =
    opts.tableNames ||
    (opts.tableCols ? Object.keys(opts.tableCols) : Object.keys(bundle.tables))
  const meta = { name: bundle.name, dbId: bundle.dbId }

  for (const name of tableNames) {
    if (!acc[name]) acc[name] = []
    const rows = bundle.tables[name] || []
    const cols = opts.tableCols?.[name] || null
    const target = acc[name]
    for (let i = 0; i < rows.length; i++) {
      target.push(rewriteRow(rows[i], bundle.localId, meta, namespaced, cols))
    }
  }
  return acc
}

/**
 * @param {Array<{
 *   localId: string
 *   name?: string
 *   dbId?: number|null
 *   tables: Record<string, Record<string, unknown>[]>
 * }>} feedBundles
 * @param {{ tableNames?: string[], forceNamespace?: boolean, tableCols?: Record<string, string[]> }} [opts]
 */
export function mergeGtfsTablesForAnalysis(feedBundles, opts = {}) {
  const bundles = (feedBundles || []).filter(
    (b) => b && b.localId && b.tables && typeof b.tables === 'object'
  )
  const namespaced = opts.forceNamespace === true || bundles.length > 1
  /** @type {Record<string, Record<string, unknown>[]>} */
  const tables = {}
  for (const bundle of bundles) {
    appendFeedTablesToMerged(tables, bundle, {
      namespaced,
      tableCols: opts.tableCols,
      tableNames: opts.tableNames,
    })
  }

  return {
    tables,
    feedCount: bundles.length,
    namespaced,
    stopCount: (tables.stops || []).length,
  }
}

/**
 * Kolon projeksiyonu (analiz panellerindeki TABLE_COLS ile uyumlu).
 * @param {Record<string, unknown>[]} rows
 * @param {string[]} cols
 */
export function projectTableRows(rows, cols) {
  if (!cols?.length) return rows || []
  return (rows || []).map((row) => {
    const out = {}
    for (const c of cols) {
      const v = row[c]
      if (v != null && v !== '') out[c] = v
    }
    if (row._feed_local_id != null) out._feed_local_id = row._feed_local_id
    if (row._feed_name != null) out._feed_name = row._feed_name
    if (row._feed_db_id != null) out._feed_db_id = row._feed_db_id
    return out
  })
}

/**
 * @param {Record<string, Record<string, unknown>[]>} tables
 * @param {Record<string, string[]>} colMap
 */
export function projectMergedTables(tables, colMap) {
  const out = {}
  for (const [name, cols] of Object.entries(colMap || {})) {
    out[name] = projectTableRows(tables[name] || [], cols)
  }
  return out
}

function asIdSet(ids) {
  if (ids instanceof Set) return ids
  return new Set((ids || []).map((id) => String(id)))
}

/**
 * `f1::R1` → { feed, local }. Namespace yoksa feed boştur.
 * @param {unknown} routeId
 */
function splitRouteId(routeId) {
  const raw = String(routeId ?? '')
  const i = raw.indexOf('::')
  if (i < 0) return { raw, feed: '', local: raw }
  return { raw, feed: raw.slice(0, i), local: raw.slice(i + 2) }
}

/**
 * Gizli hat, store'daki `hiddenRouteIds` ile eşleşir.
 * Çoklu feed'de eşleşme yalnızca `scopeFeedLocalId` feed'ine uygulanır.
 * @param {unknown} routeId
 * @param {Set<string>} hidden
 * @param {string|null|undefined} scopeFeedLocalId
 * @param {unknown} [rowFeedLocalId]
 */
export function isHiddenAnalysisRoute(routeId, hidden, scopeFeedLocalId, rowFeedLocalId) {
  if (!hidden?.size) return false
  const parts = splitRouteId(routeId)
  if (!parts.raw) return false
  const rowFeed =
    rowFeedLocalId != null && rowFeedLocalId !== ''
      ? String(rowFeedLocalId)
      : parts.feed
  if (scopeFeedLocalId) {
    const scope = String(scopeFeedLocalId)
    if (rowFeed && rowFeed !== scope) return false
  }
  return hidden.has(parts.raw) || hidden.has(parts.local)
}

/**
 * Görünen route_id kümesi trips → stop_times → shapes → stops sırasıyla iner.
 * Gizli hat yoksa tablolar aynı referansla döner.
 * @param {Record<string, Record<string, unknown>[]>} tables
 * @param {string[]|Set<string>} hiddenRouteIds
 * @param {{ scopeFeedLocalId?: string|null }} [opts]
 */
export function filterGtfsTablesToVisibleRoutes(tables, hiddenRouteIds, opts = {}) {
  const hidden = asIdSet(hiddenRouteIds)
  const scopeFeedLocalId = opts.scopeFeedLocalId || null
  if (!tables || !hidden.size) {
    return {
      tables: tables || {},
      routeCount: (tables?.routes || []).length,
      stopCount: (tables?.stops || []).length,
      filtered: false,
    }
  }

  const routeHidden = (routeId, row) =>
    isHiddenAnalysisRoute(
      routeId,
      hidden,
      scopeFeedLocalId,
      row?._feed_local_id
    )

  if (Array.isArray(tables.routes)) {
    tables.routes = tables.routes.filter((row) => !routeHidden(row?.route_id, row))
  }

  const visibleRouteIds = new Set(
    (tables.routes || [])
      .map((row) => String(row?.route_id ?? ''))
      .filter(Boolean)
  )
  const hasRoutes = Array.isArray(tables.routes)

  if (Array.isArray(tables.trips)) {
    tables.trips = tables.trips.filter((row) => {
      const rid = String(row?.route_id ?? '')
      if (!rid) return true
      if (routeHidden(rid, row)) return false
      if (hasRoutes && !visibleRouteIds.has(rid)) return false
      return true
    })
  }

  const tripIds = new Set(
    (tables.trips || []).map((row) => String(row?.trip_id ?? '')).filter(Boolean)
  )
  const shapeIds = new Set(
    (tables.trips || [])
      .map((row) => String(row?.shape_id ?? ''))
      .filter(Boolean)
  )

  if (Array.isArray(tables.stop_times)) {
    tables.stop_times = tables.stop_times.filter((row) =>
      tripIds.has(String(row?.trip_id ?? ''))
    )
  }

  if (Array.isArray(tables.shapes)) {
    tables.shapes = tables.shapes.filter((row) =>
      shapeIds.has(String(row?.shape_id ?? ''))
    )
  }

  if (Array.isArray(tables.stops) && Array.isArray(tables.stop_times)) {
    const keep = new Set(
      tables.stop_times.map((row) => String(row?.stop_id ?? '')).filter(Boolean)
    )
    for (const row of tables.stops) {
      if (!keep.has(String(row?.stop_id ?? ''))) continue
      const parent = row?.parent_station
      if (parent != null && parent !== '') keep.add(String(parent))
    }
    tables.stops = tables.stops.filter((row) => keep.has(String(row?.stop_id ?? '')))
  }

  return {
    tables,
    routeCount: (tables.routes || []).length,
    stopCount: (tables.stops || []).length,
    filtered: true,
  }
}

/**
 * Kapsam özeti: "N feed · M/K hat". Feed yoksa boş.
 * M görünür, K dahil feed'lerdeki hat sayısı. Gizlenenler aktif feed'in hatlarıdır.
 * @param {object} gtfsStore
 */
export function analysisRouteCounts(gtfsStore) {
  const included = gtfsStore?.includedFeeds || []
  const hidden = asIdSet(gtfsStore?.hiddenRouteIds)
  const activeId = gtfsStore?.activeFeedId || null
  const rawRows =
    typeof gtfsStore?.getTableRows === 'function'
      ? gtfsStore.getTableRows('routes')
      : null
  const activeRows = Array.isArray(rawRows) ? rawRows : []
  let total = 0
  let hiddenInScope = 0

  const list = included.length ? included : []
  for (const feed of list) {
    const isActive = activeId && feed.localId === activeId
    if (isActive && activeRows.length) {
      total += activeRows.length
      for (const row of activeRows) {
        if (hidden.has(String(row?.route_id ?? ''))) hiddenInScope += 1
      }
      continue
    }
    const n = Number(
      isActive
        ? (gtfsStore?.tableCounts?.routes ??
          feed.stats?.totalRoutes ??
          feed.tableCounts?.routes ??
          0)
        : (feed.stats?.totalRoutes ?? feed.tableCounts?.routes ?? 0)
    )
    const count = Number.isFinite(n) ? n : 0
    total += count
    if (isActive) hiddenInScope += Math.min(hidden.size, count)
  }

  return {
    total,
    visible: Math.max(0, total - hiddenInScope),
  }
}

/** @param {object} gtfsStore */
export function analysisScopeLabel(gtfsStore) {
  const feeds = gtfsStore?.feeds || []
  if (!feeds.length) return ''
  const included = feeds.filter((f) => f.includeInAnalysis).length
  const { visible, total } = analysisRouteCounts(gtfsStore)
  return `${included} feed · ${visible}/${total} hat`
}

/** Dahil feed + gizli hat. Sonuç varken bu anahtar değişirse rozet çıkar. */
export function analysisScopeKey(gtfsStore) {
  const included = (gtfsStore?.includedFeeds || [])
    .map((f) => f.localId)
    .join('|')
  const hidden = [...(gtfsStore?.hiddenRouteIds || [])]
    .map((id) => String(id))
    .sort()
    .join('|')
  return `${included}#${hidden}`
}

/** Üst çubuktaki feed seçiciyi açar. */
export function openGtfsFeedSelector() {
  const btn = document.querySelector('.ge-feed-selector__trigger')
  if (btn instanceof HTMLElement) btn.click()
}
